import React, { useCallback, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";

import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import TaskDetails from "../TaskCalendar/TaskDetails";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "To Do", value: "To Do" },
  { label: "In Progress", value: "In Progress" },
  { label: "Review", value: "Review" },
  { label: "Done", value: "Done" },
];

const BASIS_OPTIONS = [
  { label: "Planned", value: "planned" },
  { label: "Actual", value: "actual" },
];

const LEGEND = [
  { label: "Overdue", color: "#EF4444" },
  { label: "To Do", color: "#6B7280" },
  { label: "In Progress", color: "#3B82F6" },
  { label: "Review", color: "#F59E0B" },
  { label: "Done", color: "#10B981" },
];

const List = () => {
  const toast = useRef(null);
  const calendarRef = useRef(null);

  // Filters
  const [basis, setBasis] = useState("planned");
  const [project, setProject] = useState(null);
  const [developer, setDeveloper] = useState(null);
  const [status, setStatus] = useState(null);
  const [taskType, setTaskType] = useState(null);
  const [search, setSearch] = useState("");
  const [rangeDates, setRangeDates] = useState(null); // [from, to] for custom range view

  // Filter options
  const [projectOptions, setProjectOptions] = useState([{ label: "All Projects", value: null }]);
  const [developerOptions, setDeveloperOptions] = useState([{ label: "All Developers", value: null }]);
  const [taskTypeOptions, setTaskTypeOptions] = useState([{ label: "All Task Types", value: null }]);

  // Task detail dialog
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailsVisible, setTaskDetailsVisible] = useState(false);

  // Keep latest filters available to the FullCalendar events callback.
  const filtersRef = useRef({ basis, project, developer, status, taskType, search });
  filtersRef.current = { basis, project, developer, status, taskType, search };

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const staffResponse = await Http.get("/staff-department-wise/30");
      if (Array.isArray(staffResponse.data)) {
        const devs = staffResponse.data.map((d) => ({
          label: `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.name || `ID: ${d.id}`,
          value: d.id,
        }));
        setDeveloperOptions([{ label: "All Developers", value: null }, ...devs]);
      }

      const taskTypeResponse = await Http.get("/task-type");
      if (taskTypeResponse.data && taskTypeResponse.data.data) {
        setTaskTypeOptions([
          { label: "All Task Types", value: null },
          ...taskTypeResponse.data.data.map((t) => ({ label: t.task_type, value: t.id })),
        ]);
      }

      const projectsResponse = await Http.get("/project");
      if (projectsResponse.data && projectsResponse.data.data) {
        setProjectOptions([
          { label: "All Projects", value: null },
          ...projectsResponse.data.data.map((p) => ({ label: p.title, value: p.id })),
        ]);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load filter data",
        life: 3000,
      });
    }
  };

  // FullCalendar event source. Called automatically with the visible range
  // and again whenever refetchEvents() is invoked after a filter change.
  const fetchEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    const f = filtersRef.current;
    const params = {
      start: fetchInfo.startStr.slice(0, 10),
      end: fetchInfo.endStr.slice(0, 10),
      basis: f.basis,
    };
    if (f.project) params.project_id = f.project;
    if (f.developer) params.developer_id = f.developer;
    if (f.status) params.status = f.status;
    if (f.taskType) params.task_type_id = f.taskType;
    if (f.search) params.search = f.search;

    Http.get("/tasks/calendar-range", { params })
      .then((res) => {
        const data = res.data && res.data.data ? res.data.data : [];
        successCallback(data);
      })
      .catch((error) => {
        console.error("Error fetching calendar events:", error);
        failureCallback(error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load tasks. " + (error.response?.data?.message || error.message),
          life: 3000,
        });
      });
  }, []);

  const refetch = () => calendarRef.current?.getApi().refetchEvents();

  // Refetch when non-text filters change.
  useEffect(() => {
    refetch();
  }, [basis, project, developer, status, taskType]);

  // Debounced refetch for the search box.
  useEffect(() => {
    const t = setTimeout(() => refetch(), 400);
    return () => clearTimeout(t);
  }, [search]);

  const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const applyRange = (value) => {
    setRangeDates(value);
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (value && value[0] && value[1]) {
      const start = toLocalDateStr(value[0]);
      // FullCalendar range end is exclusive; include the picked end day.
      const endEx = new Date(value[1]);
      endEx.setDate(endEx.getDate() + 1);
      // Passing an explicit {start,end} reliably sets a custom date range.
      api.changeView("listRange", { start, end: toLocalDateStr(endEx) });
    }
  };

  const clearRange = () => {
    setRangeDates(null);
    const api = calendarRef.current?.getApi();
    if (api) api.changeView("dayGridMonth");
  };

  const resetFilters = () => {
    setBasis("planned");
    setProject(null);
    setDeveloper(null);
    setStatus(null);
    setTaskType(null);
    setSearch("");
    setRangeDates(null);
    const api = calendarRef.current?.getApi();
    if (api) {
      api.changeView("dayGridMonth");
      api.today();
    }
  };

  const handleEventClick = (info) => {
    const task = info.event.extendedProps?.task;
    if (task) {
      setSelectedTask(task);
      setTaskDetailsVisible(true);
    }
  };

  return (
    <div className="tcv2-wrap">
      <IsLogin />
      <Toast ref={toast} />

      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Task Calendar</h5>
        </div>

        <div className="am-pagebody">
          <div className="card p-3 tcv2-card">
            {/* Filter toolbar */}
            <div className="tcv2-toolbar">
              <div className="tcv2-field">
                <label>Project</label>
                <Dropdown
                  value={project}
                  options={projectOptions}
                  onChange={(e) => setProject(e.value)}
                  filter
                  placeholder="All Projects"
                  className="w-100"
                />
              </div>
              <div className="tcv2-field">
                <label>Assignee</label>
                <Dropdown
                  value={developer}
                  options={developerOptions}
                  onChange={(e) => setDeveloper(e.value)}
                  filter
                  placeholder="All Developers"
                  className="w-100"
                />
              </div>
              <div className="tcv2-field">
                <label>Status</label>
                <Dropdown
                  value={status}
                  options={STATUS_OPTIONS}
                  onChange={(e) => setStatus(e.value)}
                  placeholder="All Statuses"
                  className="w-100"
                />
              </div>
              <div className="tcv2-field">
                <label>Task Type</label>
                <Dropdown
                  value={taskType}
                  options={taskTypeOptions}
                  onChange={(e) => setTaskType(e.value)}
                  placeholder="All Task Types"
                  className="w-100"
                />
              </div>
              <div className="tcv2-field tcv2-search">
                <label>Search</label>
                <span className="p-input-icon-left w-100">
                  <i className="pi pi-search" />
                  <InputText
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search tasks..."
                    className="w-100"
                  />
                </span>
              </div>
              <div className="tcv2-field tcv2-basis">
                <label>Dates</label>
                <SelectButton
                  value={basis}
                  options={BASIS_OPTIONS}
                  onChange={(e) => e.value && setBasis(e.value)}
                  allowEmpty={false}
                />
              </div>
              <div className="tcv2-field tcv2-range">
                <label>Date Range</label>
                <div className="tcv2-range-row">
                  <Calendar
                    value={rangeDates}
                    onChange={(e) => applyRange(e.value)}
                    selectionMode="range"
                    readOnlyInput
                    showIcon
                    placeholder="From – To"
                    dateFormat="dd M yy"
                    className="tcv2-range-cal"
                  />
                  {rangeDates && (
                    <Button
                      icon="pi pi-times"
                      className="p-button-text p-button-sm"
                      onClick={clearRange}
                      tooltip="Clear range"
                      tooltipOptions={{ position: "bottom" }}
                    />
                  )}
                </div>
              </div>
              <div className="tcv2-field tcv2-reset">
                <Button
                  icon="pi pi-refresh"
                  label="Reset"
                  className="p-button-outlined"
                  onClick={resetFilters}
                />
              </div>
            </div>

            {/* Legend */}
            <div className="tcv2-legend">
              {LEGEND.map((l) => (
                <span key={l.label} className="tcv2-legend-item">
                  <span className="tcv2-dot" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>

            {/* Calendar */}
            <div className="tcv2-calendar">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                }}
                buttonText={{
                  today: "Today",
                  month: "Month",
                  week: "Week",
                  day: "Day",
                  list: "List",
                }}
                views={{
                  listRange: { type: "list", buttonText: "Range" },
                }}
                events={fetchEvents}
                eventClick={handleEventClick}
                height="auto"
                dayMaxEvents={4}
                nowIndicator
                weekNumbers
                eventDisplay="block"
                eventTimeFormat={{ hour: "2-digit", minute: "2-digit", meridiem: false }}
              />
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Task Details"
        visible={taskDetailsVisible}
        style={{ width: "80%", maxWidth: "800px" }}
        onHide={() => setTaskDetailsVisible(false)}
        dismissableMask
      >
        {selectedTask && <TaskDetails task={selectedTask} />}
      </Dialog>

      <style>{`
        .tcv2-card { border-radius: 12px; }
        .tcv2-toolbar {
          display: flex; flex-wrap: wrap; gap: 12px;
          align-items: flex-end; margin-bottom: 14px;
        }
        .tcv2-field { display: flex; flex-direction: column; min-width: 180px; }
        .tcv2-field > label {
          font-size: 0.75rem; font-weight: 600; color: #64748b;
          margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.03em;
        }
        .tcv2-search { min-width: 240px; flex: 1; }
        .tcv2-search .p-input-icon-left { display: block; position: relative; }
        .tcv2-search .p-input-icon-left > i {
          position: absolute; top: 50%; left: 12px;
          margin-top: -0.5rem; color: #94a3b8;
        }
        .tcv2-search .p-input-icon-left > .p-inputtext {
          width: 100%; padding-left: 2.25rem;
        }
        .tcv2-range { min-width: 240px; }
        .tcv2-range-row { display: flex; align-items: center; gap: 4px; }
        .tcv2-range-row .tcv2-range-cal { flex: 1; }
        .tcv2-range-row .tcv2-range-cal .p-inputtext { width: 100%; }
        .tcv2-basis, .tcv2-reset { min-width: auto; }
        .tcv2-legend {
          display: flex; flex-wrap: wrap; gap: 18px;
          padding: 10px 4px 16px; border-bottom: 1px solid #eef0f4; margin-bottom: 14px;
        }
        .tcv2-legend-item {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.8rem; color: #475569;
        }
        .tcv2-dot { width: 12px; height: 12px; border-radius: 3px; display: inline-block; }
        .tcv2-calendar .fc {
          font-size: 0.85rem;
          --fc-border-color: #eef0f4;
          --fc-today-bg-color: #eff6ff;
        }
        .tcv2-calendar .fc .fc-toolbar-title { font-size: 1.25rem; font-weight: 700; }
        .tcv2-calendar .fc .fc-button {
          background: #6366f1; border-color: #6366f1;
          text-transform: capitalize; box-shadow: none;
        }
        .tcv2-calendar .fc .fc-button:hover { background: #4f46e5; border-color: #4f46e5; }
        .tcv2-calendar .fc .fc-button-primary:not(:disabled).fc-button-active {
          background: #4338ca; border-color: #4338ca;
        }
        .tcv2-calendar .fc .fc-button:focus { box-shadow: none; }
        .tcv2-calendar .fc-event {
          border: none; border-radius: 5px;
          padding: 1px 5px; font-size: 0.78rem; cursor: pointer;
        }
        .tcv2-calendar .fc-event:hover { filter: brightness(0.95); }
        .tcv2-calendar .fc-daygrid-day-number { font-size: 0.8rem; color: #475569; }
        .tcv2-calendar .fc-col-header-cell-cushion {
          font-size: 0.78rem; text-transform: uppercase;
          letter-spacing: 0.03em; color: #64748b; padding: 8px 4px;
        }
      `}</style>
    </div>
  );
};

export default List;
